import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NumberFormatter, Menu, ActionIcon, Text, Badge, Alert } from '@mantine/core';
import moment from 'moment';
import { LuMoveRight } from 'react-icons/lu';
import { Info, Download, FileText, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { FaFilePdf } from 'react-icons/fa';

import { exportToCsv, ExportFormat } from 'utils/csv';
import { usePagination } from 'hook/usePagination';
import { IFilterState, TBookingStatus } from 'types';
import { useFlightBookingData } from 'libs/data-access';
import { EButton, EPagination, ETable, ETooltip } from 'libs/ui';
import { PAGINATION_ITEM_LIMIT } from 'libs/data-access/constant';
import { convertToBookingRef, getUserRole, parseLocalDateTime } from 'utils/utility';
import { IFlightOnlineBookingList, IFlightOnlineBookingPaymentOrder } from 'libs/data-access/types';

interface IProps {
    filterState: IFilterState;
    // HandleSearch: () => void;
};

export default function FlightBookingListing({ ...props }: IProps) {

    const navigate = useNavigate();
    const location = useLocation();
    const status: TBookingStatus = location.pathname.split("/")[2].toUpperCase() as TBookingStatus;

    const {
        getFlightBookingData
    } = useFlightBookingData();


    const [paginationQueryParams, setPaginationQueryParams] = useState<{
        id: number | null;
        date: string | null;
    }>({
        id: null,
        date: null
    });
    const [sortingDetails] = useState<{
        sortBy: "id" | "createdDate";
        sortingOrder: "ASC" | "DESC";
    }>({
        sortBy: "createdDate",
        sortingOrder: "DESC"
    });
    const [isLoadingExport, setIsLoadingExport] = useState<boolean>(false);
    const [exportStatus, setExportStatus] = useState<{
        show: boolean;
        type: 'success' | 'error';
        message: string;
    }>({
        show: false,
        type: 'success',
        message: ''
    });


    // HANDLER FUNCTIONS
    const calculateTotalFare = (paymentOrder: IFlightOnlineBookingPaymentOrder) => {

        // commission fare: sum of all the commission charges set by the admin
        const commissionFare =
            (paymentOrder?.commissionBaseFare || 0) +
            (paymentOrder?.commissionSurcharge || 0) +
            (paymentOrder?.commissionPlbBaseFare || 0) +
            (paymentOrder?.commissionPlbYQ || 0) +
            (paymentOrder?.commissionPlbYR || 0) +
            (paymentOrder?.commissionPlbBaseFareYQ || 0) +
            (paymentOrder?.commissionPlbBaseFareYR || 0);

        const commissionTdsFare = paymentOrder?.commissionTds || 0;

        // special service charges - extra baggage, meals, etc
        const ssrFare =
            (paymentOrder?.akbarOrderDetail?.akbarSsrPrice || 0) +
            (paymentOrder?.naviatorOrderDetail?.indigoSsrPrice || 0);

        // seat charges
        const seatFare =
            (paymentOrder?.akbarOrderDetail?.akbarSeatPrice || 0) +
            (paymentOrder?.naviatorOrderDetail?.indigoSeatPrice || 0);

        // base fare and charges - flight charges, admin charges, affiliate charges, taxes, etc
        const baseFareAndCharges =
            (paymentOrder?.galelioFlightOrderDetail?.flightBasePrice || 0) +
            (paymentOrder?.galelioFlightOrderDetail?.flightTaxPrice || 0) +
            (paymentOrder?.naviatorOrderDetail?.indigoBasePrice || 0) +
            (paymentOrder?.naviatorOrderDetail?.indigoTaxPrice || 0) +
            (paymentOrder?.akbarOrderDetail?.akbarBasePrice || 0) +
            (paymentOrder?.akbarOrderDetail?.akbarTaxPrice || 0) +
            (paymentOrder?.adminBaseCharge || 0) +
            (paymentOrder?.adminServiceCharge || 0) +
            (paymentOrder?.adminBookingCharge || 0) +
            (paymentOrder?.adminOtherCharge || 0) +
            (paymentOrder?.adminTotalGst || 0) +
            (paymentOrder?.affiliateBaseCharge || 0) +
            (paymentOrder?.affiliateServiceCharge || 0) +
            (paymentOrder?.affiliateBookingCharge || 0);
        // (paymentOrder?.affiliateOtherCharges || 0) +
        // (paymentOrder?.affiliateTotalGst || 0);

        // total fare: addition of all the charges and commission tds, minus commission
        return {
            totalFare: Number(
                ((
                    baseFareAndCharges +
                    ssrFare +
                    seatFare +
                    (paymentOrder?.commissionTds || 0)
                ) - commissionFare).toFixed(2)
            ),
            totalCommission: Number(commissionFare.toFixed(2)),
            totalTds: Number(commissionTdsFare || 0)
        };

    };

    const formatBookingDataForExport = (booking: IFlightOnlineBookingList[], format: ExportFormat = 'csv') => {

        const data = booking.map((trip) => ({
            bookingRef: convertToBookingRef(
                trip.bookingResponses.reduce(
                    (acc: string, item) => {
                        return `${acc}${item.id}`;
                    }, ""),
                "flight"
            ),
            route: trip?.bookingResponses?.map((item, index) =>
                item?.flightSupplierId === 3 ?
                    item?.galileoBookingDetailResponses &&
                    item?.galileoBookingDetailResponses?.map(
                        (eachFlight) => `${eachFlight?.origin} -> ${eachFlight?.destination}`
                    ).join(", ")
                    : (
                        item?.navitaireBookingDetailResponses &&
                        item?.navitaireBookingDetailResponses?.map(
                            (eachFlight) => `${eachFlight?.origin} -> ${eachFlight?.destination}`
                        ).join(", ")
                    )
            )?.join("\n"),
            pnr: trip?.bookingResponses?.map((item) =>
                `PNR: ${item?.pnrDetail?.supplierPnr || "N/A"} ${item?.galileoBookingDetailResponses && item?.galileoBookingDetailResponses?.length ? `GDS PNR: ${item?.pnrDetail?.providerPnr || "N/A"} ${item?.pnrDetail?.ticketNumber ? `Ticket: ${item?.pnrDetail?.ticketNumber || "N/A"}` : ""}`
                    :
                    ""
                }
            `)?.join(","),
            bookingData: trip?.bookingResponses[0]?.bookingDate
                ? parseLocalDateTime(trip?.bookingResponses[0]?.bookingDate)
                : "--/--/----",
            supplier: trip?.bookingResponses?.map((item) => `${item?.flightSupplierName}, `).join(", "),
            travelDate: trip?.bookingResponses[0]?.flightSupplierId === 3 ?
                trip?.bookingResponses[0]
                    ?.galileoBookingDetailResponses &&
                    trip?.bookingResponses[0]
                        ?.galileoBookingDetailResponses[0]
                        ?.flightBookingSegments[0]?.departureTime
                    ? moment(
                        trip?.bookingResponses[0]
                            ?.galileoBookingDetailResponses[0]
                            ?.flightBookingSegments[0]?.departureTime
                    ).format("DD/MM/YYYY")
                    : "--/--/----"
                : trip?.bookingResponses[0]
                    ?.navitaireBookingDetailResponses &&
                    trip?.bookingResponses[0]
                        ?.navitaireBookingDetailResponses[0]
                        ?.navitaireBookingDetailSegments[0]?.originDateTime
                    ? moment(
                        trip?.bookingResponses[0]
                            ?.navitaireBookingDetailResponses[0]
                            ?.navitaireBookingDetailSegments[0]
                            ?.originDateTime
                    ).format("DD/MM/YYYY")
                    : "--/--/----",
            bookedBy: `${trip?.bookedBy?.toLowerCase().replaceAll("_", " ")}, ${trip?.userResponse?.companyName}, ${trip?.userResponse?.name?.firstName} ${trip?.userResponse?.name?.lastName || ""}, ${trip?.userResponse?.emailId}, ${trip?.userResponse?.phoneNumber || ""}`,
            paymentAdvice: trip?.bookingResponses[0]?.invoiceNo,
            bookingAmount: calculateTotalFare(trip?.paymentOrder).totalFare,
            totalCommission: calculateTotalFare(trip?.paymentOrder).totalCommission,
            totalTds: calculateTotalFare(trip?.paymentOrder).totalTds
        }));

        const headers = [
            { label: "Booking Ref.", key: "bookingRef" },
            { label: "Route", key: "route" },
            { label: "PNR. No.", key: "pnr" },
            { label: "Booking Date", key: "bookingData" },
            { label: "Supplier", key: "supplier" },
            { label: "Travel Date", key: "travelDate" },
            { label: "Booked By", key: "bookedBy" },
            { label: "Payment Advice", key: "paymentAdvice" },
            { label: "Booking Amount", key: "bookingAmount" },
            { label: "Total Commission", key: "totalCommission" },
            { label: "Total TDS", key: "totalTds" },
        ];

        // console.log("data: ", data);
        // console.log("headers: ", headers);

        try {
            exportToCsv<any>(
                `${status}-flight-booking-list-${moment().format("DD-MM-YYYY HH:mm")}`,
                data,
                headers,
                format
            );

            setExportStatus({
                show: true,
                type: 'success',
                message: `Data exported successfully as ${format.toUpperCase()} file`
            });

            // Hide success message after 3 seconds
            setTimeout(() => {
                setExportStatus(prev => ({ ...prev, show: false }));
            }, 3000);
        } catch (error) {
            setExportStatus({
                show: true,
                type: 'error',
                message: 'Failed to export data. Please try again.'
            });

            // Hide error message after 5 seconds
            setTimeout(() => {
                setExportStatus(prev => ({ ...prev, show: false }));
            }, 5000);
        }
    };


    // API CALLS
    const fetchFlightBookingList = async (format: ExportFormat = 'csv') => {
        setIsLoadingExport(true);

        let paginationPath = `?paginationAction=${"INITIAL_PAGE"}&maxLimit=${PAGINATION_ITEM_LIMIT}&id=${''}&bookingStatus=${status ? status : props.filterState?.bookingStatus?.value}&sortingOrder=${sortingDetails.sortingOrder}&sortby=${sortingDetails.sortBy}`;
        const requestEndPoint = `${paginationPath}`;

        try {
            const response = await getFlightBookingData(requestEndPoint);
            if (response.isSuccess) {
                // console.log("Data fetched for export: ", response.data?.data);
                formatBookingDataForExport(response.data?.data, format);
            } else {
                console.log("Error while fetching data for export: ", response.error);
                setExportStatus({
                    show: true,
                    type: 'error',
                    message: 'Failed to fetch data for export. Please try again.'
                });

                setTimeout(() => {
                    setExportStatus(prev => ({ ...prev, show: false }));
                }, 5000);
            };
        } catch (error) {
            console.error("Export error: ", error);
            setExportStatus({
                show: true,
                type: 'error',
                message: 'An error occurred during export. Please try again.'
            });

            setTimeout(() => {
                setExportStatus(prev => ({ ...prev, show: false }));
            }, 5000);
        };

        setIsLoadingExport(false);
    };

    const {
        dataList: flightBookingList,
        isLoading,
        disablePrev,
        disableNext,
        setTriggerNextPage,
        setTriggerPreviousPage
    } = usePagination<IFlightOnlineBookingList>({
        fetchListData: getFlightBookingData,
        maxLimit: PAGINATION_ITEM_LIMIT,
        // sortby: sortingDetails?.sortBy,
        sortingOrder: sortingDetails?.sortingOrder,
        extraQueryParams: {
            dateType: props.filterState?.dateType?.value || null,
            bookedBy: props.filterState?.bookedBy?.value || null,
            pnr: props.filterState?.pnr?.value || null,
            lastName: props.filterState?.lastName?.value || null,
            referenceNo: props.filterState?.referenceNo?.value || null,
            ticketNo: props.filterState?.ticketNo?.value || null,
            bookingStatus: status ? status : props.filterState?.bookingStatus?.value || null,
            startDate: props.filterState?.startDate?.value || null,
            endDate: props.filterState?.endDate?.value || null,
        } as Record<string, string | number | boolean>,
        paginationParams: paginationQueryParams as Record<string, string | number | boolean>,
        dependencies: []
    });


    return (
        <div>
            {/* EXPORT DATA SECTION */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <Download className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
                            <p className="text-sm text-gray-600">Download booking data in CSV format</p>
                        </div>
                    </div>

                    {/* <Badge
                        color="blue"
                        variant="light"
                        size="lg"
                        className="px-3 py-1"
                    >
                        {flightBookingList?.length || 0} Records
                    </Badge> */}
                </div>

                {/* Export Format Selection */}
                <div className="flex items-center gap-4">
                    <Text size="sm" className="font-medium text-gray-700">Export Data:</Text>

                    <EButton
                        variant="outline"
                        leftSection={<Download className="w-4 h-4" />}
                        loading={isLoadingExport}
                        className="min-w-[140px]"
                        onClick={() => fetchFlightBookingList('csv')}
                        disabled={isLoadingExport}
                    >
                        {isLoadingExport ? 'Exporting...' : 'Download Data'}
                    </EButton>

                    {/* <Menu shadow="md" width={200}>
                        <Menu.Target>
                            <div>
                                <EButton
                                    variant="outline"
                                    leftSection={<Download className="w-4 h-4" />}
                                    loading={isLoadingExport}
                                    className="min-w-[140px]"
                                >
                                    {isLoadingExport ? 'Exporting...' : 'Download Data'}
                                </EButton>
                            </div>
                        </Menu.Target>

                        <Menu.Dropdown>
                            <Menu.Label>Choose Export Format</Menu.Label>
                            <Menu.Item
                                leftSection={<FileText className="w-4 h-4 text-green-600" />}
                                onClick={() => fetchFlightBookingList('csv')}
                                disabled={isLoadingExport}
                            >
                                <div>
                                    <div className="font-medium">CSV Format</div>
                                    <div className="text-xs text-gray-500">Comma-separated values</div>
                                </div>
                            </Menu.Item>

                            <Menu.Item
                                leftSection={<FileSpreadsheet className="w-4 h-4 text-green-600" />}
                                onClick={() => fetchFlightBookingList('excel')}
                                disabled={isLoadingExport}
                            >
                                <div>
                                    <div className="font-medium">Excel Format</div>
                                    <div className="text-xs text-gray-500">Microsoft Excel (.xlsx)</div>
                                </div>
                            </Menu.Item>

                            <Menu.Item
                                leftSection={<FaFilePdf className="w-4 h-4 text-red-600" />}
                                onClick={() => fetchFlightBookingList('pdf')}
                                disabled={isLoadingExport}
                            >
                                <div>
                                    <div className="font-medium">PDF Format</div>
                                    <div className="text-xs text-gray-500">Portable Document Format</div>
                                </div>
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu> */}

                    {/* Quick Export Buttons */}
                    {/* <div className="flex items-center gap-2 ml-4">
                        <Text size="xs" className="text-gray-500">Quick:</Text>
                        <ActionIcon
                            variant="light"
                            color="green"
                            size="sm"
                            onClick={() => fetchFlightBookingList('csv')}
                            disabled={isLoadingExport}
                            title="Export as CSV"
                        >
                            <FileText className="w-4 h-4" />
                        </ActionIcon>
                        <ActionIcon
                            variant="light"
                            color="green"
                            size="sm"
                            onClick={() => fetchFlightBookingList('excel')}
                            disabled={isLoadingExport}
                            title="Export as Excel"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                        </ActionIcon>
                        <ActionIcon
                            variant="light"
                            color="red"
                            size="sm"
                            onClick={() => fetchFlightBookingList('pdf')}
                            disabled={isLoadingExport}
                            title="Export as PDF"
                        >
                            <FaFilePdf className="w-4 h-4" />
                        </ActionIcon>
                    </div> */}
                </div>

                {/* Export Status Alert */}
                {exportStatus.show && (
                    <Alert
                        icon={exportStatus.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        color={exportStatus.type === 'success' ? 'green' : 'red'}
                        variant="light"
                        className="mt-4"
                        onClose={() => setExportStatus(prev => ({ ...prev, show: false }))}
                        withCloseButton
                    >
                        {exportStatus.message}
                    </Alert>
                )}
            </div>

            {/* FLIGHT BOOKINGS TABLE */}
            <ETable loading={isLoading}>
                <thead>
                    <tr>
                        <th></th>
                        <th>Ref.</th>
                        <th>Route</th>
                        <th>PNR. No.</th>
                        <th>Booking Date</th>
                        {getUserRole().includes("ADMIN") && <th>Supplier</th>}
                        <th>Travel Date</th>
                        <th>Booked By</th>
                        <th>Payment Advice</th>
                        {/* <th>Status</th> */}
                        <th className="flex justify-end">Booking Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {flightBookingList?.map((trip, index) => (
                        <tr key={"trip" + index}>
                            <td>
                                <EButton
                                    size="xs"
                                    variant="default"
                                    onClick={() => {
                                        navigate(`/online-booking/flight/details/${trip.uuid}`, {
                                            state: { filterState: props.filterState, trip },
                                        });
                                    }}
                                >
                                    Manage
                                </EButton>
                            </td>
                            <td>
                                {convertToBookingRef(
                                    trip.bookingResponses.reduce(
                                        (acc: string, item) => {
                                            return `${acc}${item.id}`;
                                        }, ""),
                                    "flight"
                                )}
                            </td>
                            <td>
                                {
                                    trip?.bookingResponses?.map((item) =>
                                        item.flightSupplierId === 4
                                            ?
                                            item.akbarBookingDetailResponses?.map((eachFlight) => (
                                                <p className="font-medium text-slate-700">
                                                    {eachFlight?.origin} <LuMoveRight className="inline-block mx-0.5 text-slate-600" /> {eachFlight?.destination}
                                                </p>
                                            ))
                                            :
                                            item?.flightSupplierId === 3
                                                ?
                                                item?.galileoBookingDetailResponses &&
                                                item?.galileoBookingDetailResponses?.map(
                                                    (eachFlight) => (
                                                        <p className="font-medium text-slate-700">
                                                            {eachFlight?.origin} <LuMoveRight className="inline-block mx-0.5 text-slate-600" /> {eachFlight?.destination}
                                                        </p>
                                                    )
                                                )
                                                :
                                                item?.navitaireBookingDetailResponses &&
                                                item?.navitaireBookingDetailResponses?.map(
                                                    (eachFlight) => (
                                                        <p className="font-medium text-slate-700">
                                                            {eachFlight?.origin} <LuMoveRight className="inline-block mx-0.5 text-slate-600" />  {eachFlight?.destination}
                                                        </p>
                                                    )
                                                ))
                                }
                            </td>
                            <td>
                                <div className="flex flex-col gap-1">
                                    {
                                        trip?.bookingResponses?.map((item) => (
                                            <div className="text-xs font-medium">
                                                <div>
                                                    PNR: {item?.pnrDetail?.supplierPnr?.split("|")[0] || "N/A"}
                                                </div>
                                                {
                                                    item?.galileoBookingDetailResponses && item?.galileoBookingDetailResponses?.length
                                                        ?
                                                        <>
                                                            {
                                                                item?.pnrDetail?.supplierPnr
                                                                    ?
                                                                    <div>
                                                                        GDS PNR: {item?.pnrDetail?.providerPnr?.split("|")[0] || "N/A"}
                                                                    </div>
                                                                    :
                                                                    <></>
                                                            }
                                                            {
                                                                item?.pnrDetail?.ticketNumber
                                                                    ?
                                                                    <div className='flex gap-1 items-center'>
                                                                        Ticket:
                                                                        <div>
                                                                            {item?.pnrDetail?.ticketNumber.split("/").map((val,idx)=>(<p key={idx} className='text-xs'>{val}</p>)) || "N/A"}
                                                                        </div> 
                                                                    </div>
                                                                    :
                                                                    <></>
                                                            }
                                                        </>
                                                        :
                                                        <></>
                                                }
                                            </div>
                                        ))
                                    }
                                </div>
                            </td>
                            <td>
                                {trip?.bookingResponses[0]?.bookingDate
                                    ? parseLocalDateTime(trip?.bookingResponses[0]?.bookingDate)
                                    : "--/--/----"}
                            </td>
                            {getUserRole().includes("ADMIN") &&
                            <td>
                                {trip?.bookingResponses?.map((item) => (
                                    <>
                                        <span className="capitalize mr-1">
                                            {item?.flightSupplierName}
                                        </span><span className="last:hidden -ml-1">, </span>
                                    </>
                                ))}
                            </td>
                            }
                            <td>
                                {trip?.bookingResponses[0]?.flightSupplierId === 4 // akbar
                                    ?
                                    <>
                                        {trip?.bookingResponses[0]
                                            ?.akbarBookingDetailResponses &&
                                            trip?.bookingResponses[0]
                                                ?.akbarBookingDetailResponses?.[0]
                                                ?.akbarBookingDetailJourneys?.[0]?.departureTime
                                            ? moment(
                                                trip?.bookingResponses[0]
                                                    ?.akbarBookingDetailResponses?.[0]
                                                    ?.akbarBookingDetailJourneys?.[0]?.departureTime
                                            ).format("DD/MM/YYYY")
                                            : "--/--/----"}</>
                                    :
                                    trip?.bookingResponses[0]?.flightSupplierId === 3 // galileo
                                        ?
                                        <>
                                            {trip?.bookingResponses[0]
                                                ?.galileoBookingDetailResponses &&
                                                trip?.bookingResponses[0]
                                                    ?.galileoBookingDetailResponses[0]
                                                    ?.flightBookingSegments[0]?.departureTime
                                                ? moment(
                                                    trip?.bookingResponses[0]
                                                        ?.galileoBookingDetailResponses[0]
                                                        ?.flightBookingSegments[0]?.departureTime
                                                ).format("DD/MM/YYYY")
                                                : "--/--/----"}
                                        </>
                                        :
                                        <>
                                            {trip?.bookingResponses[0]
                                                ?.navitaireBookingDetailResponses &&
                                                trip?.bookingResponses[0]
                                                    ?.navitaireBookingDetailResponses[0]
                                                    ?.navitaireBookingDetailSegments[0]?.originDateTime
                                                ? moment(
                                                    trip?.bookingResponses[0]
                                                        ?.navitaireBookingDetailResponses[0]
                                                        ?.navitaireBookingDetailSegments[0]
                                                        ?.originDateTime
                                                ).format("DD/MM/YYYY")
                                                : "--/--/----"}
                                        </>
                                }
                            </td>
                            <td className="">
                                <ETooltip
                                    radius="md"
                                    label={
                                        <div className="bg-gray-800 text-white p-4 rounded-lg shadow-xl max-w-sm">
                                            {/* Header Section */}
                                            <div className="border-b border-gray-600 pb-3 mb-3">
                                                <div className="font-bold text-base capitalize mb-1">
                                                    {trip?.bookedBy?.toLowerCase().replaceAll("_", " ")}
                                                </div>
                                                {trip?.userResponse?.companyName && (
                                                    <div className="text-blue-300 text-sm font-medium">
                                                        {trip?.userResponse?.companyName}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Primary Contact Info */}
                                            <div className="space-y-2 mb-3">
                                                <div className="text-white text-sm font-semibold">
                                                    {`${trip?.userResponse?.name?.firstName} ${trip?.userResponse?.name?.lastName}`}
                                                </div>
                                                <div className="text-gray-300 text-xs">
                                                    📧 {trip?.userResponse?.emailId}
                                                </div>
                                                <div className="text-gray-300 text-xs">
                                                    📞 {trip?.userResponse?.phoneNumber || "N/A"}
                                                </div>
                                            </div>

                                            {/* Address Section */}
                                            {(trip?.userResponse?.address || trip?.userResponse?.city || trip?.userResponse?.pinCode) && (
                                                <div className="border-t border-gray-600 pt-3">
                                                    <div className="text-gray-400 text-xs font-medium mb-2 uppercase tracking-wide">
                                                        Address
                                                    </div>
                                                    <div className="text-gray-300 text-xs space-y-1">
                                                        {trip?.userResponse?.address && (
                                                            <div>📍 {trip?.userResponse?.address}</div>
                                                        )}
                                                        {(trip?.userResponse?.city || trip?.userResponse?.pinCode) && (
                                                            <div>
                                                                {trip?.userResponse?.city}
                                                                {trip?.userResponse?.city && trip?.userResponse?.pinCode && " - "}
                                                                {trip?.userResponse?.pinCode}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    position="bottom"
                                    withArrow
                                    offset={8}
                                >
                                    <div className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-2 rounded-md transition-colors group">
                                        <div className="flex-1">
                                            <div className="capitalize font-medium">
                                                {trip?.bookedBy?.toLowerCase().replaceAll("_", " ")}
                                            </div>
                                            <div className="text-slate-600 text-sm">
                                                {trip?.userResponse?.name?.firstName + " " + trip?.userResponse?.name?.lastName}
                                            </div>
                                        </div>
                                        <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-700 transition-colors" />
                                    </div>
                                </ETooltip>
                            </td>
                            <td className="">
                                <div className="">
                                    {trip?.bookingResponses[0]?.invoiceNo}
                                </div>
                            </td>
                            {/* <td>
                                <EBadge
                                    variant="light"
                                    color={
                                        trip?.bookingResponses[0]?.bookingStatus?.replaceAll(
                                            "_",
                                            " "
                                        ) === "SUCCESS"
                                            ? "green"
                                            : trip?.bookingResponses[0]?.bookingStatus?.replaceAll(
                                                "_",
                                                " "
                                            ) === "PENDING"
                                                ? "yellow"
                                                : "red"
                                    }
                                >{trip?.bookingResponses[0]?.bookingStatus?.replaceAll("_", " ")}</EBadge>
                            </td> */}
                            <td className="text-right font-semibold">
                                <NumberFormatter
                                    thousandSeparator
                                    prefix="₹ "
                                    value={calculateTotalFare(trip?.paymentOrder).totalFare}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </ETable>

            <EPagination
                isLoading={isLoading}
                disablePrev={disablePrev}
                disableNext={disableNext}
                goToNextPage={() => {
                    setPaginationQueryParams({
                        ...paginationQueryParams,
                        date: flightBookingList[flightBookingList.length - 1].bookingResponses?.[0]?.bookingDate
                    });
                    setTriggerNextPage(true);
                }}
                goToPreviousPage={() => {
                    setPaginationQueryParams({
                        ...paginationQueryParams,
                        date: flightBookingList[0].bookingResponses?.[0]?.bookingDate
                    });

                    setTriggerPreviousPage(true);
                }}
            />
        </div>
    );

}